import { IOS } from "../activity-logs/labels";

/**
 * Agregações da aba Fornecedores. A distribuição real é muito concentrada (mediana
 * de centenas de reais, máximo de centenas de milhares), por isso a classificação
 * ABC, a mediana em vez da média e as faixas de crédito.
 */

export const ABC = {
  A: { key: "A", label: "Classe A", hint: "Até 80% do crédito", color: IOS.mint },
  B: { key: "B", label: "Classe B", hint: "De 80% a 95% do crédito", color: IOS.indigo },
  C: { key: "C", label: "Classe C", hint: "Últimos 5% do crédito", color: IOS.gray },
};

const median = (values) => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

// Faixas em ordem de grandeza, porque os valores vão de centavos a centenas de milhares
const BANDS = [
  { key: "a", short: "Até R$ 100", tiny: "≤ 100", label: "Até R$ 100", max: 100 },
  { key: "b", short: "R$ 100–1 mil", tiny: "100–1 mil", label: "De R$ 100 a R$ 1 mil", max: 1000 },
  { key: "c", short: "R$ 1–10 mil", tiny: "1–10 mil", label: "De R$ 1 mil a R$ 10 mil", max: 10000 },
  { key: "d", short: "R$ 10–100 mil", tiny: "10–100 mil", label: "De R$ 10 mil a R$ 100 mil", max: 100000 },
  { key: "e", short: "+ R$ 100 mil", tiny: "+ 100 mil", label: "Acima de R$ 100 mil", max: Infinity },
];

export const TOP_BARS = 6;

// Cores das barras: uma por item, vivas mas na mesma opacidade baixa dos gráficos
export const CHART_COLORS = [IOS.mint, IOS.blue, IOS.indigo, IOS.purple, IOS.pink, IOS.orange];

// Reduz uma série longa a N pontos, mantendo o primeiro e o último
const sample = (values, n) => {
  if (values.length <= n) return values;
  return Array.from({ length: n }, (_, i) => values[Math.round((i * (values.length - 1)) / (n - 1))]);
};

export const buildSupplierStats = (suppliers) => {
  const sorted = [...suppliers].sort((a, b) => b.total - a.total);
  const total = sorted.reduce((sum, s) => sum + s.total, 0);

  let cumulative = 0;
  const enriched = sorted.map((s, index) => {
    // A classe vem do acumulado ANTES do fornecedor: quem cruza 80% ainda é A
    let abc = "C";
    if (cumulative < total * 0.8) abc = "A";
    else if (cumulative < total * 0.95) abc = "B";
    cumulative += s.total;
    return {
      ...s,
      abc,
      rank: index + 1,
      share: total > 0 ? (s.total / total) * 100 : 0,
      cumulativeShare: total > 0 ? (cumulative / total) * 100 : 0,
      perItem: s.count > 0 ? s.total / s.count : 0,
    };
  });

  const classes = Object.values(ABC).map((meta) => {
    const members = enriched.filter((s) => s.abc === meta.key);
    const value = members.reduce((sum, s) => sum + s.total, 0);
    return { ...meta, count: members.length, total: value, share: total > 0 ? (value / total) * 100 : 0 };
  });

  const bands = BANDS.map((band, i) => {
    const min = i === 0 ? -Infinity : BANDS[i - 1].max;
    const members = enriched.filter((s) => s.total >= min && s.total < band.max);
    const value = members.reduce((sum, s) => sum + s.total, 0);
    return { ...band, count: members.length, total: value, share: total > 0 ? (value / total) * 100 : 0 };
  });
  const small = bands.slice(0, 2);
  const topBars = enriched.slice(0, TOP_BARS);

  const supplierCount = enriched.length;
  const forEighty = classes[0].count;

  return {
    suppliers: enriched,
    total,
    classes,
    bands,
    topBars,
    topBarsShare: topBars.reduce((sum, s) => sum + s.share, 0),
    smallCount: small.reduce((sum, b) => sum + b.count, 0),
    smallShare: small.reduce((sum, b) => sum + b.share, 0),
    supplierCount,
    forEighty,
    eightyShare: supplierCount > 0 ? (forEighty / supplierCount) * 100 : 0,
    medianCredit: median(enriched.map((s) => s.total)),
    medianPerItem: median(enriched.filter((s) => s.count > 0).map((s) => s.perItem)),
    averageCredit: supplierCount > 0 ? total / supplierCount : 0,
    groups: new Set(enriched.filter((s) => s.cnpj).map((s) => s.cnpj.replace(/\D/g, "").slice(0, 8))).size,
    multiTax: enriched.filter((s) => Object.keys(s.byTax).length > 1).length,
    // Séries curtas para as sparklines dos KPIs (esta API não tem série mensal por fornecedor)
    concentrationCurve: sample(enriched.map((s) => s.cumulativeShare), 40),
    taxSpread: [1, 2, 3, 4].map((n) => enriched.filter((s) => Object.keys(s.byTax).length === n).length),
    // Mais retorno por item entre quem tem valor relevante; a classe C distorceria o ranking
    bestReturn: enriched
      .filter((s) => s.abc !== "C" && s.count > 0)
      .sort((a, b) => b.perItem - a.perItem)
      .slice(0, 5),
  };
};
